import { useState } from 'react'
import { useLanguage } from '@/i18n/LanguageContext'
import {
  Bot, Download, Plus, Pencil, Trash2, Copy, GripVertical,
  ArrowUp, ArrowDown, ScrollText, MousePointerClick, Clock, X,
  Chrome, ExternalLink
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription
} from '@/components/ui/dialog'
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select'
import { toast } from 'sonner'
import {
  useScripts, useCreateScript, useUpdateScript, useDeleteScript,
  type Script, type ScriptStep
} from '@/api/hooks/useCheckerBot'

function emptyStep(type: ScriptStep['type'] = 'scroll'): ScriptStep {
  if (type === 'scroll') return { type: 'scroll', count: 5, delay: 2 }
  if (type === 'click') return { type: 'click', selector: '', label: '' }
  return { type: 'wait', seconds: 3 }
}

function stepIcon(type: ScriptStep['type']) {
  if (type === 'scroll') return <ScrollText className="h-4 w-4" />
  if (type === 'click') return <MousePointerClick className="h-4 w-4" />
  return <Clock className="h-4 w-4" />
}

function stepLabel(type: ScriptStep['type']) {
  if (type === 'scroll') return 'Scroll'
  if (type === 'click') return 'Clique'
  return 'Espera'
}

function StepEditor({
  step, index, total,
  onChange, onRemove, onMove,
}: {
  step: ScriptStep
  index: number
  total: number
  onChange: (s: ScriptStep) => void
  onRemove: () => void
  onMove: (dir: -1 | 1) => void
}) {
  const { t } = useLanguage()
  return (
    <div className="flex items-start gap-2 rounded-lg border bg-muted/30 p-3">
      <div className="flex flex-col items-center gap-1 pt-1">
        <span className="text-xs font-bold text-muted-foreground">{index + 1}</span>
        <GripVertical className="h-4 w-4 text-muted-foreground/50" />
      </div>

      <div className="flex-1 space-y-2">
        <div className="flex items-center gap-2">
          <Select
            value={step.type}
            onValueChange={(v) => onChange(emptyStep(v as ScriptStep['type']))}
          >
            <SelectTrigger className="w-[130px] h-8 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="scroll">
                <ScrollText className="h-3 w-3 mr-1 inline" /> Scroll
              </SelectItem>
              <SelectItem value="click">
                <MousePointerClick className="h-3 w-3 mr-1 inline" /> {t('Clique')}
              </SelectItem>
              <SelectItem value="wait">
                <Clock className="h-3 w-3 mr-1 inline" /> {t('Espera')}
              </SelectItem>
            </SelectContent>
          </Select>

          <div className="ml-auto flex gap-1">
            <Button
              variant="ghost" size="icon" className="h-7 w-7"
              disabled={index === 0}
              onClick={() => onMove(-1)}
            >
              <ArrowUp className="h-3 w-3" />
            </Button>
            <Button
              variant="ghost" size="icon" className="h-7 w-7"
              disabled={index === total - 1}
              onClick={() => onMove(1)}
            >
              <ArrowDown className="h-3 w-3" />
            </Button>
            <Button
              variant="ghost" size="icon" className="h-7 w-7 text-destructive hover:text-destructive"
              onClick={onRemove}
            >
              <X className="h-3 w-3" />
            </Button>
          </div>
        </div>

        {step.type === 'scroll' && (
          <div className="flex gap-3">
            <div className="flex-1">
              <Label className="text-xs">{t('Quantidade')}</Label>
              <Input
                type="number" min={1} max={50}
                className="h-8 text-xs mt-1"
                value={step.count ?? 5}
                onChange={(e) => onChange({ ...step, count: parseInt(e.target.value) || 1 })}
              />
            </div>
            <div className="flex-1">
              <Label className="text-xs">{t('Delay (seg)')}</Label>
              <Input
                type="number" min={0.5} max={10} step={0.5}
                className="h-8 text-xs mt-1"
                value={step.delay ?? 2}
                onChange={(e) => onChange({ ...step, delay: parseFloat(e.target.value) || 1 })}
              />
            </div>
          </div>
        )}

        {step.type === 'click' && (
          <div className="space-y-2">
            <div>
              <Label className="text-xs">{t('Seletor CSS')}</Label>
              <Input
                className="h-8 text-xs mt-1 font-mono"
                placeholder="#js_cf > span > div"
                value={step.selector ?? ''}
                onChange={(e) => onChange({ ...step, selector: e.target.value })}
              />
            </div>
            <div>
              <Label className="text-xs">{t('Descricao (opcional)')}</Label>
              <Input
                className="h-8 text-xs mt-1"
                placeholder="Ex: Abrir menu, Ver stories..."
                value={step.label ?? ''}
                onChange={(e) => onChange({ ...step, label: e.target.value })}
              />
            </div>
          </div>
        )}

        {step.type === 'wait' && (
          <div className="w-1/2">
            <Label className="text-xs">{t('Segundos')}</Label>
            <Input
              type="number" min={1} max={30}
              className="h-8 text-xs mt-1"
              value={step.seconds ?? 3}
              onChange={(e) => onChange({ ...step, seconds: parseInt(e.target.value) || 1 })}
            />
          </div>
        )}
      </div>
    </div>
  )
}

function ScriptDialog({
  open, onOpenChange,
  editScript,
}: {
  open: boolean
  onOpenChange: (v: boolean) => void
  editScript: Script | null
}) {
  const { t } = useLanguage()
  const [name, setName] = useState(editScript?.name ?? '')
  const [url, setUrl] = useState(editScript?.url ?? '')
  const [steps, setSteps] = useState<ScriptStep[]>(editScript?.steps ?? [])

  const createMut = useCreateScript()
  const updateMut = useUpdateScript()
  const saving = createMut.isPending || updateMut.isPending

  // Reset form when dialog opens with new data
  const handleOpenChange = (v: boolean) => {
    if (v) {
      setName(editScript?.name ?? '')
      setUrl(editScript?.url ?? '')
      setSteps(editScript?.steps ?? [])
    }
    onOpenChange(v)
  }

  const handleSave = () => {
    if (!name.trim()) { toast.error(t('Informe o nome do roteiro')); return }
    if (!url.trim()) { toast.error(t('Informe a URL')); return }
    if (steps.length === 0) { toast.error(t('Adicione pelo menos um passo')); return }

    const data = { name: name.trim(), url: url.trim(), steps }

    if (editScript) {
      updateMut.mutate({ id: editScript.id, ...data }, {
        onSuccess: () => { toast.success(t('Roteiro atualizado!')); handleOpenChange(false) },
        onError: (e) => toast.error(e.message),
      })
    } else {
      createMut.mutate(data, {
        onSuccess: () => { toast.success(t('Roteiro criado!')); handleOpenChange(false) },
        onError: (e) => toast.error(e.message),
      })
    }
  }

  const updateStep = (i: number, s: ScriptStep) => {
    const next = [...steps]
    next[i] = s
    setSteps(next)
  }

  const removeStep = (i: number) => setSteps(steps.filter((_, idx) => idx !== i))

  const moveStep = (i: number, dir: -1 | 1) => {
    const next = [...steps]
    const j = i + dir
    ;[next[i], next[j]] = [next[j], next[i]]
    setSteps(next)
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{editScript ? t('Editar Roteiro') : t('Novo Roteiro')}</DialogTitle>
          <DialogDescription>
            {t('Monte o roteiro de acoes que a extensao vai executar.')}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <Label>{t('Nome do roteiro')}</Label>
              <Input
                className="mt-1"
                placeholder="Ex: Checar Stories RealityShows"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>
            <div>
              <Label>{t('URL da pagina')}</Label>
              <Input
                className="mt-1"
                placeholder="https://www.facebook.com/NomeDaPagina"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
              />
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <Label>{t('Passos')} ({steps.length})</Label>
              <Button
                variant="outline" size="sm" className="h-7 text-xs gap-1"
                onClick={() => setSteps([...steps, emptyStep('scroll')])}
              >
                <Plus className="h-3 w-3" /> {t('Adicionar Passo')}
              </Button>
            </div>

            {steps.length === 0 ? (
              <div className="rounded-lg border border-dashed p-6 text-center text-sm text-muted-foreground">
                {t('Nenhum passo adicionado. Clique em "+ Adicionar Passo" acima.')}
              </div>
            ) : (
              <div className="space-y-2 max-h-[40vh] overflow-y-auto pr-1">
                {steps.map((step, i) => (
                  <StepEditor
                    key={i}
                    step={step}
                    index={i}
                    total={steps.length}
                    onChange={(s) => updateStep(i, s)}
                    onRemove={() => removeStep(i)}
                    onMove={(dir) => moveStep(i, dir)}
                  />
                ))}
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)}>
            {t('Cancelar')}
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? t('Salvando...') : t('Salvar')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function ScriptCard({
  script,
  onEdit,
  onDelete,
}: {
  script: Script
  onEdit: () => void
  onDelete: () => void
}) {
  const { t } = useLanguage()
  const copyToClipboard = () => {
    const exportData = {
      name: script.name,
      url: script.url,
      steps: script.steps,
    }
    navigator.clipboard.writeText(JSON.stringify(exportData, null, 2))
    toast.success(t('Roteiro copiado! Cole na extensao com "Importar Roteiro".'))
  }

  const stepSummary = script.steps.reduce(
    (acc, s) => {
      acc[s.type] = (acc[s.type] || 0) + 1
      return acc
    },
    {} as Record<string, number>,
  )

  return (
    <Card className="p-4 hover:border-primary/30 transition-colors">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <h3 className="font-semibold text-sm truncate">{script.name}</h3>
          <p className="text-xs text-muted-foreground truncate mt-0.5">{script.url}</p>
          <div className="flex flex-wrap gap-1.5 mt-2">
            {stepSummary.scroll && (
              <Badge variant="secondary" className="text-xs gap-1">
                <ScrollText className="h-3 w-3" /> {stepSummary.scroll}x {t('scroll')}
              </Badge>
            )}
            {stepSummary.click && (
              <Badge variant="secondary" className="text-xs gap-1">
                <MousePointerClick className="h-3 w-3" /> {stepSummary.click}x {t('clique')}
              </Badge>
            )}
            {stepSummary.wait && (
              <Badge variant="secondary" className="text-xs gap-1">
                <Clock className="h-3 w-3" /> {stepSummary.wait}x {t('espera')}
              </Badge>
            )}
          </div>
        </div>
      </div>

      <div className="flex gap-2 mt-3 pt-3 border-t">
        <Button variant="outline" size="sm" className="h-7 text-xs gap-1 flex-1" onClick={onEdit}>
          <Pencil className="h-3 w-3" /> {t('Editar')}
        </Button>
        <Button variant="outline" size="sm" className="h-7 text-xs gap-1 flex-1" onClick={copyToClipboard}>
          <Copy className="h-3 w-3" /> {t('Copiar pro Extension')}
        </Button>
        <Button variant="outline" size="sm" className="h-7 text-xs gap-1 text-destructive hover:text-destructive" onClick={onDelete}>
          <Trash2 className="h-3 w-3" />
        </Button>
      </div>
    </Card>
  )
}

export function CheckerBot() {
  const { t } = useLanguage()
  const { data: scripts = [], isLoading } = useScripts()
  const deleteMut = useDeleteScript()

  const [dialogOpen, setDialogOpen] = useState(false)
  const [editScript, setEditScript] = useState<Script | null>(null)

  const openCreate = () => {
    setEditScript(null)
    setDialogOpen(true)
  }

  const openEdit = (script: Script) => {
    setEditScript(script)
    setDialogOpen(true)
  }

  const handleDelete = (script: Script) => {
    if (!confirm(`${t('Excluir')} "${script.name}"?`)) return
    deleteMut.mutate(script.id, {
      onSuccess: () => toast.success(t('Roteiro deletado')),
      onError: (e) => toast.error(e.message),
    })
  }

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
            <Bot className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h1 className="text-xl font-bold">Checker Bot</h1>
            <p className="text-sm text-muted-foreground">
              {t('Monte roteiros de checagem e exporte pra extensao')}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <a href="/checker-extension.zip" download>
            <Button variant="outline" size="sm" className="gap-1.5">
              <Download className="h-4 w-4" />
              <span className="hidden sm:inline">{t('Baixar Extensao')}</span>
            </Button>
          </a>
          <Button size="sm" className="gap-1.5" onClick={openCreate}>
            <Plus className="h-4 w-4" />
            {t('Novo Roteiro')}
          </Button>
        </div>
      </div>

      {/* Scripts List */}
      {isLoading ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {[1, 2, 3].map((i) => (
            <Card key={i} className="p-4 animate-pulse">
              <div className="h-4 bg-muted rounded w-3/4 mb-2" />
              <div className="h-3 bg-muted rounded w-full mb-3" />
              <div className="flex gap-1.5">
                <div className="h-5 bg-muted rounded w-16" />
                <div className="h-5 bg-muted rounded w-16" />
              </div>
            </Card>
          ))}
        </div>
      ) : scripts.length === 0 ? (
        <Card className="p-12 text-center">
          <div className="flex justify-center mb-4">
            <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-muted">
              <ScrollText className="h-8 w-8 text-muted-foreground" />
            </div>
          </div>
          <h2 className="text-lg font-semibold mb-1">{t('Nenhum roteiro')}</h2>
          <p className="text-sm text-muted-foreground mb-4">
            {t('Crie seu primeiro roteiro de checagem para exportar pra extensao Chrome.')}
          </p>
          <Button onClick={openCreate} className="gap-1.5">
            <Plus className="h-4 w-4" /> {t('Novo Roteiro')}
          </Button>
        </Card>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {scripts.map((script) => (
            <ScriptCard
              key={script.id}
              script={script}
              onEdit={() => openEdit(script)}
              onDelete={() => handleDelete(script)}
            />
          ))}
        </div>
      )}

      {/* How it works */}
      <Card className="p-5">
        <h2 className="mb-3 text-base font-semibold flex items-center gap-2">
          <Chrome className="h-4 w-4" />
          {t('Como usar')}
        </h2>
        <div className="grid gap-4 sm:grid-cols-4">
          <div className="rounded-lg bg-muted/50 p-4">
            <div className="text-2xl mb-2">1.</div>
            <div className="text-sm font-medium">{t('Crie o roteiro')}</div>
            <div className="text-xs text-muted-foreground mt-1">
              {t('Monte os passos: scroll, clique em seletores CSS, esperas entre acoes.')}
            </div>
          </div>
          <div className="rounded-lg bg-muted/50 p-4">
            <div className="text-2xl mb-2">2.</div>
            <div className="text-sm font-medium">{t('Copie o JSON')}</div>
            <div className="text-xs text-muted-foreground mt-1">
              {t('Clique em "Copiar pro Extension" pra copiar o roteiro pra area de transferencia.')}
            </div>
          </div>
          <div className="rounded-lg bg-muted/50 p-4">
            <div className="text-2xl mb-2">3.</div>
            <div className="text-sm font-medium">{t('Importe na extensao')}</div>
            <div className="text-xs text-muted-foreground mt-1">
              {t('Na extensao, clique em "Importar Roteiro" e cole o JSON copiado.')}
            </div>
          </div>
          <div className="rounded-lg bg-muted/50 p-4">
            <div className="text-2xl mb-2">4.</div>
            <div className="text-sm font-medium">{t('Execute')}</div>
            <div className="text-xs text-muted-foreground mt-1">
              {t('Selecione o roteiro e clique Executar. A extensao faz tudo automaticamente.')}
            </div>
          </div>
        </div>
      </Card>

      {/* Dialog */}
      {dialogOpen && (
        <ScriptDialog
          open={dialogOpen}
          onOpenChange={setDialogOpen}
          editScript={editScript}
        />
      )}
    </div>
  )
}
